import { c as _c } from "react/compiler-runtime";
import { useAuth0, User } from "@auth0/auth0-react";
import { useContext, useMemo } from "react";
import { Info } from "react-feather";
import { Distribution, Donation, TaxYearlyReport, TaxYearlyReportUnits } from "../../../../models";
import { useDistributions, useDonations, useTaxUnits, useYearlyTaxReports } from "../../../../_queries";
import { InfoBox } from "../../../shared/components/Infobox/Infobox";
import { Spinner } from "../../../shared/components/Spinner/Spinner";
import { DonorContext } from "../../layout/donorProvider";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { TaxYearlyReportList } from "../../shared/lists/taxYearlyReportsList/TaxYearlyReportList";
import { TaxYearlyReportMobileList } from "../../shared/lists/taxYearlyReportsList/TaxYearlyReportMobileList";
import styles from "./YearlyReportsTab.module.scss";
import { AggregatedImpactTableConfiguration } from "../../donations/DonationsAggregateImpactTable/DonationsAggregateImpactTable";
export const YearlyReportsTab: React.FC<{
  aggregatedImpactConfiguration: AggregatedImpactTableConfiguration;
}> = t0 => {
  const $ = _c(27);
  const {
    aggregatedImpactConfiguration
  } = t0;
  const {
    getAccessTokenSilently,
    user
  } = useAuth0();
  const {
    donor
  } = useContext(DonorContext);
  const {
    loading: reportsLoading,
    data: reportsData,
    error: reportsError
  } = useYearlyTaxReports(user as User, getAccessTokenSilently);
  const {
    loading: donationsLoading,
    data: donations
  } = useDonations(user as User, getAccessTokenSilently);
  let kidsSet;
  if ($[0] !== donations) {
    kidsSet = new Set();
    donations?.forEach(donation => kidsSet.add(donation.KID));
    $[0] = donations;
    $[1] = kidsSet;
  } else {
    kidsSet = $[1];
  }
  let t1;
  if ($[2] !== kidsSet) {
    t1 = Array.from(kidsSet);
    $[2] = kidsSet;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const kids = t1;
  const shouldFetchDistributions = !donationsLoading && donations !== undefined && kids.length > 0;
  const {
    loading: distributionsLoading,
    data: distributions
  } = useDistributions(user as User, getAccessTokenSilently, shouldFetchDistributions, kids);
  const donationsLoaded = Array.isArray(donations);
  const reportsLoaded = Array.isArray(reportsData);
  let t2;
  let t3;
  if ($[4] !== aggregatedImpactConfiguration || $[5] !== distributions || $[6] !== distributionsLoading || $[7] !== donations || $[8] !== donationsLoaded || $[9] !== donationsLoading || $[10] !== donor || $[11] !== reportsData || $[12] !== reportsError || $[13] !== reportsLoaded || $[14] !== reportsLoading || $[15] !== shouldFetchDistributions) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolvedDistributions = shouldFetchDistributions ? distributions : [];
      const distributionsLoaded = Array.isArray(resolvedDistributions);
      const dataAvailable = donationsLoaded && reportsLoaded && distributionsLoaded;
      const loading = donationsLoading || shouldFetchDistributions && distributionsLoading || reportsLoading;
      if (reportsError) {
        let t4;
        let t5;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <h4 className={styles.header}>Dine årsoppgaver</h4>;
          t5 = <p>Henting av dine årsrapporter feilet. Våre utviklere er varlset om feilen og er på saken. Kontakt oss gjerne på donasjon@gieffektivt.no så finner vi ut av det!{" "}</p>;
          $[18] = t4;
          $[19] = t5;
        } else {
          t4 = $[18];
          t5 = $[19];
        }
        let t6;
        if ($[20] !== reportsError) {
          t6 = JSON.stringify(reportsError, null, 2);
          $[20] = reportsError;
          $[21] = t6;
        } else {
          t6 = $[21];
        }
        let t7;
        if ($[22] !== t6) {
          t7 = <div className={styles.container}>{t4}{t5}<ErrorMessage>{t6}</ErrorMessage></div>;
          $[22] = t6;
          $[23] = t7;
        } else {
          t7 = $[23];
        }
        t3 = t7;
        break bb0;
      }
      if (loading || !dataAvailable || !donor) {
        let t4;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div className={styles.container}><h4 className={styles.header}>Dine årsoppgaver</h4><div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}><Spinner /></div></div>;
          $[24] = t4;
        } else {
          t4 = $[24];
        }
        t3 = t4;
        break bb0;
      }
      if (reportsData.length === 0) {
        let t4;
        if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <div className={styles.container}><h4 className={styles.header}>Dine årsoppgaver</h4><p>No data available.</p></div>;
          $[25] = t4;
        } else {
          t4 = $[25];
        }
        t3 = t4;
        break bb0;
      }
      const distributionsMap = new Map();
      resolvedDistributions.map(dist => distributionsMap.set(dist.kid, dist));
      let t4;
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <h4 className={styles.header}>Dine årsoppgaver</h4>;
        $[26] = t4;
      } else {
        t4 = $[26];
      }
      t2 = <div className={styles.container}>{t4}{reportsData.map(report => {
          const sumEanWithoutDeduction = report.sumDonationsWithoutTaxUnit.channels.filter(_temp).reduce(_temp2, 0);
          const sumEanWithDeduction = report.units.map(_temp4).reduce(_temp5, 0);
          const sumEanDonations = sumEanWithDeduction + sumEanWithoutDeduction;
          const eanDistribution = {
            kid: "EAN Giverportal",
            taxUnitId: null,
            donorId: parseInt(donor.id),
            causeAreas: [{
              id: -1,
              percentageShare: "100",
              standardSplit: true,
              organizations: [{
                id: 1,
                name: "GiveWell",
                percentageShare: "100"
              }]
            }]
          };
          distributionsMap.set("EAN Giverportal", eanDistribution);
          const fakeEanDonation = {
            id: Number.MAX_SAFE_INTEGER,
            KID: "EAN Giverportal",
            sum: sumEanDonations.toString(),
            timestamp: new Date(2022, 6, 1).toISOString(),
            donor: donor.name,
            donorId: parseInt(donor.id),
            email: donor.email,
            paymentMethod: "Bank",
            transactionCost: "0",
            metaOwnerId: 3
          };
          const mergedDonations = [...donations, fakeEanDonation];
          const filteredDonations = mergedDonations.filter(donation_0 => new Date(donation_0.timestamp).getFullYear() === report.year);
          const currentYear = new Date().getFullYear();
          return <>{report.sumDonationsWithoutTaxUnit.sumDonations > 0 && report.year === currentYear - 1 && <InfoBox><header><Info />Donasjoner mangler skattenhet</header><p>{report.units.length == 0 ? "Registrer en skatteenhet i fanen til venstre i menyen under \"skatt\" og alle donasjoner vil knyttes til den. Ta kontakt p\xE5 donasjon@gieffektivt.no om du \xF8nsker \xE5 knytte donasjonene dine til flere skatteenheter." : "Du har allerede en eller flere skatteenheter. Kontakt oss p\xE5 donasjon@gieffektivt.no for \xE5 knytte donasjonene dine til rett skatteenhet."}</p></InfoBox>}<div className={styles.desktopList}><TaxYearlyReportList key={report.year} donations={filteredDonations} distribtionMap={distributionsMap} report={report} aggregateImpactConfig={aggregatedImpactConfiguration} /></div><div className={styles.mobileList}><TaxYearlyReportMobileList key={report.year} donations={donations.filter(donation_1 => new Date(donation_1.timestamp).getFullYear() === report.year && distributionsMap.get(donation_1.KID)?.taxUnitId)} distribtionMap={distributionsMap} report={report} aggregateImpactConfig={aggregatedImpactConfiguration} /></div></>;
        })}</div>;
    }
    $[4] = aggregatedImpactConfiguration;
    $[5] = distributions;
    $[6] = distributionsLoading;
    $[7] = donations;
    $[8] = donationsLoaded;
    $[9] = donationsLoading;
    $[10] = donor;
    $[11] = reportsData;
    $[12] = reportsError;
    $[13] = reportsLoaded;
    $[14] = reportsLoading;
    $[15] = shouldFetchDistributions;
    $[16] = t2;
    $[17] = t3;
  } else {
    t2 = $[16];
    t3 = $[17];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  return t2;
};
function _temp(c) {
  return c.channel === "EAN Giverportal";
}
function _temp2(acc, missing) {
  if (missing.channel === "EAN Giverportal") {
    return acc + missing.sumDonations;
  }
  return acc;
}
function _temp3(c_0) {
  return c_0.channel === "EAN Giverportal";
}
function _temp4(u) {
  return u.channels.find(_temp3);
}
function _temp5(acc_0, channel) {
  if (channel) {
    return acc_0 + channel?.sumDonations || 0;
  }
  return acc_0;
}